import { useEffect, useRef, useState, type RefObject } from "react";
import { isDefaultConversationTitle } from "../i18n";
import type { Language } from "./languages";
import type { Conversation, WorkspaceState } from "../types";
import { loadConversation } from "./storage";
import { getClientId } from "./client-id";
import { fetchWithDeadline, readResponseJson } from "./request-errors";
import { cleanConversationTitle, isLegacyAutomaticTitle, titleMessages } from "./conversation-title";

interface Options {
  workspace: WorkspaceState;
  workspaceRef: RefObject<WorkspaceState>;
  hydrated: boolean;
  language: Language;
  updateConversation: (id: string, update: (conversation: Conversation) => Conversation) => void;
}

/** One background naming request at a time; typing and answer streaming never wait for it. */
export function useConversationTitles({ workspace, workspaceRef, hydrated, language, updateConversation }: Options) {
  const active = useRef<AbortController | undefined>(undefined);
  const retries = useRef(new Map<string, { count: number; after: number }>());
  const [revision, wake] = useState(0);
  useEffect(() => () => active.current?.abort(), []);
  useEffect(() => {
    if (active.current?.signal.aborted) active.current = undefined;
    if (!hydrated || active.current) return;
    const eligible = workspace.conversations.filter(conversation =>
      conversation.titleSource !== "manual" && conversation.titleSource !== "ai"
      && !(conversation.messagesLoaded === false && conversation.pendingGeneration) && !conversation.messages.some(message => message.status === "streaming")
      && (conversation.messages.length || conversation.messageCount));
    const next = eligible.find(conversation => {
      const retry = retries.current.get(conversation.id);
      return !retry || (retry.count < 3 && retry.after <= Date.now());
    });
    if (!next) {
      const upcoming = eligible.map(conversation => retries.current.get(conversation.id)).filter(retry => retry && retry.count < 3);
      if (!upcoming.length) return;
      const timer = window.setTimeout(() => wake(value => value + 1), Math.max(1, Math.min(...upcoming.map(retry => retry!.after)) - Date.now()));
      return () => window.clearTimeout(timer);
    }
    const controller = new AbortController();
    active.current = controller;
    void (async () => {
      try {
        const stored = next.messagesLoaded === false ? await loadConversation(next.id) : next;
        if (controller.signal.aborted) return;
        if (!stored) throw new Error("conversation_unavailable");
        const firstUser = stored.messages.find(message => message.role === "user");
        if (!firstUser || (next.titleSource !== "pending" && !isDefaultConversationTitle(next.title)
          && !isLegacyAutomaticTitle(next.title, firstUser.content))) {
          updateConversation(next.id, current => current.title === next.title && current.titleSource === next.titleSource ? { ...current, titleSource: "manual" } : current);
          return;
        }
        const response = await fetchWithDeadline("/api/chat/title", {
          method: "POST", signal: controller.signal,
          headers: { "content-type": "application/json", "x-neurondeck-client": getClientId() },
          body: JSON.stringify({ language, messages: titleMessages(stored.messages) }),
        }, 30_000);
        if (!response.ok) throw new Error("title_unavailable");
        const data = await readResponseJson(response, controller.signal, 30_000) as { title?: unknown } | null;
        const title = typeof data?.title === "string" ? cleanConversationTitle(data.title) : "";
        if (!title) throw new Error("empty_title");
        if (controller.signal.aborted) return;
        // A manual rename, edit, new turn or deletion while naming wins over this result.
        const current = workspaceRef.current.conversations.find(conversation => conversation.id === next.id);
        if (!current || current.title !== next.title || current.titleSource !== next.titleSource) return;
        if (current.messagesLoaded !== false && (current.messages.find(message => message.role === "user")?.id !== firstUser.id
          || current.messages.at(-1)?.id !== stored.messages.at(-1)?.id)) return;
        updateConversation(next.id, conversation => ({ ...conversation, title, titleSource: "ai" }));
        retries.current.delete(next.id);
      } catch {
        if (!controller.signal.aborted) {
          const count = (retries.current.get(next.id)?.count ?? 0) + 1;
          retries.current.set(next.id, { count, after: Date.now() + count * 60_000 });
        }
      } finally {
        if (active.current === controller) active.current = undefined;
        if (!controller.signal.aborted) wake(value => value + 1);
      }
    })();
  }, [workspace, workspaceRef, hydrated, language, updateConversation, revision]);
}
